<template>
  <div class="seller-performance-report">
    <breadcrumbs :breadcrumbsData="breadcrumbsDataComputed" #icon>
      <svg class="icon" aria-hidden="true" style="fill: #fff">
        <use xlink:href="#icon-shangpin"></use>
      </svg>
    </breadcrumbs>
    <noticeTool type="performance"/>
    <a-spin :spinning="spinning">
      <div class="content-main" ref="mainRef">
        <div class="content-head">
          <div class="content-head-left">
            <artmisads-segmented-tabs
              v-model="activeTab"
              :items="programConfig"
            />
            <p>
              <span v-if="tagNumParams.lastUpdateAt">
                {{ st('common.lastUpdateAt') }}
                {{
                  moment(tagNumParams.lastUpdateAt).format('YYYY-MM-DD hh:mm')
                }}
              </span>
            </p>
          </div>

          <a-dropdown :getPopupContainer="() => mainRef">
            <template #overlay>
              <artmisads-dropdown-menu @click="handleExportClick">
                <a-menu-item key="all">{{ st('reports.performance.exportAllData') }}</a-menu-item>
                <a-menu-item key="filter">{{ st('reports.performance.exportWithFilters') }}</a-menu-item>
              </artmisads-dropdown-menu>
            </template>
            <artmisads-button-special>
              <svg class="icon daochu" aria-hidden="true">
                <use xlink:href="#icon-daochu"></use>
              </svg>
              <span class="btn-text">{{ st('reports.performance.exportButton') }}</span>
              <svg class="icon artmisads-select-down-icon" aria-hidden="true">
                <use xlink:href="#icon-jiantou-xia-cu"></use>
              </svg>
            </artmisads-button-special>
          </a-dropdown>
        </div>
        <filterBar
          :params="params"
          :dateParams="[
            params.startReportDateTimestamp,
            params.endReportDateTimestamp,
          ]"
          :getList="getList"
          :setParams="setParams"
          platform="seller"
        />
        <div class="tags">
          <filterTag
            :naList="naList"
            :setParams="setParams"
            :tagNumParams="tagNumParams"
            :marketplace="params.marketplace"
            :defaultMetricName="defaultMetricName"
            :noNeedList="noNeedList"
          />
        </div>

        <div class="content">
          <p>{{ metricChartTitle }}</p>
          <div class="chart">
            <chart
              :dataList="dataList"
              :width="chartWidth"
              showYName="metricValue"
              showXName="reportDateTimestamp"
              :lineColor="chartParams.lineColor"
              :lineBgOffsetTop="chartParams.lineBgOffsetTop"
              :lineBgOffsetZero="chartParams.lineBgOffsetZero"
              chartType="line"
              :selectDaysNum="selectDaysNum"
              :showCurrent="false"
              :defaultMetricName="defaultMetricName"
              :isPercent="params.metricName === 'acosAmount'"
              :startTimestamp="params.startReportDateTimestamp"
              :endTimestamp="params.endReportDateTimestamp"
              :metricName="params.metricName"
            />
          </div>
          <div class="table">
            <tableContent
              :dataList="tableList"
              :tableNaKeys="naList"
              :marketplace="params.marketplace"
              :activeTab="activeTab"
              :tableTotal="tableTotal"
              :params="tableParams"
              :setParams="setTableParams"
              :tagNumParams="tagNumParams"
            />
          </div>
        </div>
      </div>
    </a-spin>
  </div>
</template>
<script setup>
import { computed, onMounted, ref, watch, nextTick } from 'vue'
import moment from 'moment'

import { reportsApi } from '~/server/index'
import { RESPORTS_TYPE_MAP } from '../../../../contants/reports'
import breadcrumbs from '../../breadcrumbs.vue'
import filterBar from '~/components/reports/filterBar.vue'
import filterTag from '~/components/reports/filterTag.vue'
import noticeTool from '~/components/reports/noticeTool.vue'
import chart from '~/components/reports/chart.vue'
import tableContent from './tableContent.vue'
import { useUserInfoStore } from '@/storeData'
import { CURRENCY_MAPPER } from '@/contants/common'
import { useSystemStore } from '@/storeData/system'
import { useSellerI18n } from '@/composables/useSellerI18n'

const systemStore = useSystemStore()
const { st } = useSellerI18n()

const breadcrumbsDataComputed = computed(() => ({
  title: st('reports.performance.pageTitle'),
  color: '#4682FA',
  links: [
    {
      name: st('common.home'),
      path: '/seller/dashboard',
    },
    {
      name: st('reports.performance.breadcrumbReports'),
      path: '/seller/reports/performance',
    },
    {
      name: st('reports.performance.breadcrumbPerformance'),
      path: '/seller/reports/performance',
    },
  ],
}))
const activeTab = ref('0')
const programConfig = computed(() => {
  const src = systemStore.programConfig || []
  const tabs = src.map((item, idx) => {
    if (idx === 0) return { ...item, label: st('common.amazonAttribution') }
    if (idx === 1) return { ...item, label: st('common.amazonAssociate') }
    return { ...item }
  })
  // 仅 Performance 页单独追加 Creator Connections
  if (!tabs.some((item) => item.key === '2')) {
    tabs.push({
      key: '2',
      label: st('menu.creatorConnections'),
      disabled: false,
    })
  }
  return tabs
})
const user = useUserInfoStore()
const chartWidth = ref(1092)
const naList = ref([]) // filter tag显示N/A列表
const noNeedList = ref([
  'orders',
  'conversionsRate',
  'commissionAmount',
  'quantity',
  'ccCommissionAmount',
  'finalCommissionAndFeeAmount',
  'finalCommissionAmount',
])
const defaultMetricName = ref('clicks')
const spinning = ref(false)
const dataList = ref([])
const mainRef = ref()
const countryCode = user?.userInfo?.countryCode || 'US'
// console.log(moment().startOf('day').valueOf(),'数据开始时间')
const params = ref({
  marketplace: CURRENCY_MAPPER[countryCode].host,
  startReportDateTimestamp: moment()
    .subtract(30, 'day')
    .startOf('day')
    .valueOf(),
  endReportDateTimestamp: moment().startOf('day').valueOf(),
  brandId: null,
  productId: null,
  metricName: 'clicks',
  asin: null,
})

/** Maps chart metric keys to seller.reports.* i18n paths (suffix after seller.) */
const METRIC_TITLE_KEY = {
  clicks: 'reports.performanceTable.clicks',
  detailPageViews: 'reports.performance.chartMetricDetailPageViews',
  addToCart: 'reports.performanceTable.addToCart',
  conversions: 'reports.performanceTable.conversions',
  orders: 'reports.performanceTable.orders',
  quantity: 'reports.performanceTable.quantity',
  conversionsRate: 'reports.performance.chartMetricConversionsRate',
  acosAmount: 'reports.performanceTable.acos',
  salesAmount: 'reports.performanceTable.sales',
  discountedSalesAmount: 'reports.performanceTable.salesForCommission',
  commissionAndFeeAmount: 'reports.performanceTable.commission',
  commissionAmount: 'reports.performanceTable.commission',
  bonusAmount: 'reports.performanceTable.bonus',
  finalCommissionAndFeeAmount: 'reports.performanceTable.adjustedCommissionByAcc',
  finalCommissionAmount: 'reports.performanceTable.adjustedCommissionByAcc',
}

const metricChartTitle = computed(() => {
  const metricName = params.value.metricName
  const i18nKey = METRIC_TITLE_KEY[metricName]
  if (i18nKey) return st(i18nKey)
  return RESPORTS_TYPE_MAP[metricName]?.name || metricName || ''
})

const tableList = ref([])
const tableTotal = ref(0)
const tableParams = ref({
  page: 1,
  size: 200,
  sortDirection: '',
  sortField: '',
})

const tagNumParams = ref({
  totalAcos: null,
  totalClicks: null,
  totalDetailPageViews: null,
  totalAddToCart: null,
  totalConversions: null,
  totalOrders: null,
  totalSalesAmount: null,
  totalDiscountedSalesAmount: null,
  totalBonusAmount: null,
  totalCommissionAndFeeAmount: null,
  lastUpdateAt: null,
})

const selectDaysNum = computed(() => {
  const { endReportDateTimestamp, startReportDateTimestamp } = params.value
  const intervalDays = moment(endReportDateTimestamp).diff(
    moment(startReportDateTimestamp),
    'days',
  )
  return intervalDays
})

const chartParams = computed(() => {
  let lineColor,
    lineBgOffsetTop,
    lineBgOffsetZero,
    isPercent,
    chartParamsData,
    itemData
  itemData = RESPORTS_TYPE_MAP[params.value.metricName]
  lineColor = itemData.lineColor
  lineBgOffsetTop = itemData.lineBgOffsetTop
  lineBgOffsetZero = itemData.lineBgOffsetZero
  isPercent = params.value.metricName === 'commission'
  chartParamsData = { lineColor, lineBgOffsetTop, lineBgOffsetZero, isPercent }
  return chartParamsData
})

const getChartWidth = () => {
  const $chart = document.querySelector('.chart')
  const widthNum = $chart.clientWidth - 20
  chartWidth.value = widthNum
}

const setTableParams = (obj) => {
  const newObj = { ...tableParams.value, ...obj }
  tableParams.value = newObj
}

const setParams = (obj) => {
  const newObj = { ...params.value, ...obj }
  setTableParams({ page: 1 })
  params.value = newObj
}

const isResetting = ref(false)

watch(
  params,
  async (newValue, oldValue) => {
    if (isResetting.value) {
      return
    }

    if (activeTab.value === '0') {
      if (newValue.metricName !== oldValue.metricName) {
        defaultMetricName.value = newValue.metricName
        spinning.value = true
        await getList()
        spinning.value = false
      } else {
        if (newValue.brandId || newValue.asin) {
          naList.value = ['clicks', 'bonusAmount']
          newValue.metricName = 'detailPageViews'
          defaultMetricName.value = 'detailPageViews'
        } else {
          naList.value = []
          newValue.metricName = 'clicks'
          defaultMetricName.value = 'clicks'
        }
        getAllData()
      }
    }
    if (activeTab.value === '1') {
      naList.value = []
      if (newValue.metricName !== oldValue.metricName) {
        defaultMetricName.value = newValue.metricName
        spinning.value = true
        await getListAssociate()
        spinning.value = false
      } else {
        getAllDataAssociate()
      }
    }
    if (activeTab.value === '2') {
      naList.value = []
      if (newValue.metricName !== oldValue.metricName) {
        defaultMetricName.value = newValue.metricName
        spinning.value = true
        await getListCc()
        spinning.value = false
      } else {
        getAllDataCc()
      }
    }
  },
  { deep: true, flush: 'post' },
)

watch(
  tableParams,
  async (newValue, oldValue) => {
    if (isResetting.value) return
    // 检查值是否真的发生变化
    const hasChanged = Object.keys(newValue).some(
      (key) => newValue[key] !== oldValue[key],
    )
    if (hasChanged) {
      switch (activeTab.value) {
        case '0':
          spinning.value = true
          await getTableList()
          spinning.value = false
          break
        case '1':
          spinning.value = true
          await getTableListAssociate()
          spinning.value = false
          break
        case '2':
          spinning.value = true
          await getTableListCc()
          spinning.value = false
          break
      }
    }
  },
  { deep: true, flush: 'post' },
)
// 添加对activeTab的监听
watch(activeTab, async () => {
  // 当tab切换时，重新获取所有数据
  await reserParams()
  await reserTableParams()
  switch (activeTab.value) {
    case '0':
      noNeedList.value = [
        'orders',
        'conversionsRate',
        'commissionAmount',
        'quantity',
        'finalCommissionAndFeeAmount',
        'finalCommissionAmount',
      ]
      if (params.value.brandId || params.value.asin) {
        naList.value = ['clicks']
        defaultMetricName.value = 'detailPageViews'
      } else {
        naList.value = []
        defaultMetricName.value = 'clicks'
      }
      getAllData()
      break
    case '1':
      noNeedList.value = [
        'clicks',
        'detailPageViews',
        'addToCart',
        'conversions',
        'orders',
        'conversionsRate',
        'acosAmount',
        'discountedSalesAmount',
        'bonusAmount',
        'commissionAmount',
        'finalCommissionAmount',
      ]
      defaultMetricName.value = 'quantity'
      getAllDataAssociate()
      break
    case '2':
      noNeedList.value = [
        'detailPageViews',
        'addToCart',
        'conversions',
        'conversionsRate',
        'quantity',
        'acosAmount',
        'discountedSalesAmount',
        'commissionAndFeeAmount',
        'commissionAmount',
        'ccCommissionAmount',
        'finalCommissionAndFeeAmount',
        'finalCommissionAmount',
      ]
      defaultMetricName.value = 'clicks'
      getAllDataCc()
      break
  }
})
const getList = async () => {
  let newparams = { ...params.value }

  if (newparams.metricName === 'acosAmount') {
    newparams.metricName = 'acos'
  }

  const res = await reportsApi.getSellerCommissionReportsPerformanceDailyMetric(
    newparams,
  )
  const { success, data } = res
  if (success) {
    dataList.value =
      newparams.metricName === 'acos'
        ? data.map((item) => {
            return {
              ...item,
              metricValue: (item.metricValue * 100)
                .toFixed(2)
                .replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1'),
            }
          })
        : data
  }
}
const getListAssociate = async () => {
  let newparams = { ...params.value }
  const res =
    await reportsApi.getSellerCommissionReportsPerformanceDailyMetricAssociate(
      newparams,
    )
  const { success, data } = res
  if (success) {
    dataList.value = data
  }
}
const getListCc = async () => {
  const newparams = { ...params.value }
  const res =
    await reportsApi.getSellerCommissionReportsPerformanceDailyMetricCc(
      newparams,
    )
  const { success, data } = res
  if (success) {
    dataList.value = data
  }
}
const getTableList = async () => {
  let newparams = { ...params.value, ...tableParams.value }
  const res = await reportsApi.getSellerCommissionReportsPerformance(newparams)
  const { success, data } = res
  if (success) {
    tableList.value = data.data
    tableTotal.value = data.total
  }
}
const getTableListAssociate = async () => {
  let newparams = { ...params.value, ...tableParams.value }
  const res = await reportsApi.getSellerCommissionReportsPerformanceAssociate(
    newparams,
  )
  const { success, data } = res
  if (success) {
    tableList.value = data.data
    tableTotal.value = data.total
  }
}
const getTableListCc = async () => {
  const newparams = { ...params.value, ...tableParams.value }
  const res = await reportsApi.getSellerCommissionReportsPerformanceCc(
    newparams,
  )
  const { success, data } = res
  if (success) {
    tableList.value = data.data
    tableTotal.value = data.total
  }
}
const getSellerCommissionReportsAgg = async () => {
  const res = await reportsApi.getSellerCommissionReportsAgg(params.value)
  const { success, data } = res
  if (success) {
    tagNumParams.value = data
  }
}
const getSellerCommissionReportsAggAssociate = async () => {
  const res = await reportsApi.getSellerCommissionReportsAggAssociate(
    params.value,
  )
  const { success, data } = res
  if (success) {
    tagNumParams.value = data
  }
}
const getSellerCommissionReportsAggCc = async () => {
  const res = await reportsApi.getSellerCommissionReportsAggCc(params.value)
  const { success, data } = res
  if (success) {
    tagNumParams.value = {
      ...data,
      totalOrders: data.totalOrders ?? data.totalConversions ?? 0,
      totalClicks: data.totalClicks ?? 0,
    }
  }
}

const reserParams = async () => {
  isResetting.value = true
  nextTick(() => {
    params.value = {
      marketplace: CURRENCY_MAPPER[countryCode].host,
      startReportDateTimestamp: moment()
        .subtract(30, 'day')
        .startOf('day')
        .valueOf(),
      endReportDateTimestamp: moment().startOf('day').valueOf(),
      brandId: null,
      productId: null,
      metricName: activeTab.value === '0' ? 'clicks' : activeTab.value === '1' ? 'quantity' : 'clicks',
      asin: null,
    }
    nextTick(() => {
      isResetting.value = false
    })
  })
}
const reserTableParams = async () => {
  isResetting.value = true
  nextTick(() => {
    tableParams.value = {
      page: 1,
      size: 200,
      sortDirection: '',
      sortField: '',
    }
    nextTick(() => {
      isResetting.value = false
    })
  })
}
const handleExportClick = async ({ key }) => {
  let exportParams = {
      endReportDateTimestamp: moment().startOf('day').valueOf(),
      marketplace: params.value.marketplace,
      startReportDateTimestamp: moment()
        .subtract(365, 'day')
        .startOf('day')
        .valueOf(),
    },
    newExportParams = {}
  if (key === 'filter') {
    exportParams = { ...params.value }
  }
  for (let item in exportParams) {
    if (exportParams[item]) {
      newExportParams[item] = exportParams[item]
    }
  }

  const finalParams = {
    qs: newExportParams,
    fileName: `Performance-Reports-${moment().format('YYYY-MM-DD')}.csv`,
  }
  switch (activeTab.value) {
    case '0':
      finalParams.fileName = `Performance-Reports-Attribution-${moment().format(
        'YYYY-MM-DD',
      )}.csv`
      reportsApi.exportCommissionPerformanceReports(finalParams)

      break
    case '1':
      finalParams.fileName = `Performance-Reports-Associate-${moment().format(
        'YYYY-MM-DD',
      )}.csv`
      reportsApi.exportCommissionPerformanceReportsAssociate(finalParams)

      break
    case '2':
      finalParams.fileName = `Performance-Reports-CreatorConnections-${moment().format(
        'YYYY-MM-DD',
      )}.csv`
      reportsApi.exportCommissionPerformanceReportsCc(finalParams)

      break
  }
}
const getAllData = async () => {
  spinning.value = true
  try {
    const results = await Promise.allSettled([
      getSellerCommissionReportsAgg(),
      getList(),
      getTableList(),
    ])

    // 检查是否有失败的请求
    const failedResults = results.filter(
      (result) => result.status === 'rejected',
    )
    if (failedResults.length > 0) {
      console.error('部分请求执行失败:', failedResults)
    }
  } catch (error) {
    console.error('请求执行失败:', error)
  } finally {
    spinning.value = false
  }
}
const getAllDataAssociate = async () => {
  spinning.value = true
  try {
    const results = await Promise.allSettled([
      getSellerCommissionReportsAggAssociate(),
      getListAssociate(),
      getTableListAssociate(),
    ])

    // 检查是否有失败的请求
    const failedResults = results.filter(
      (result) => result.status === 'rejected',
    )
    if (failedResults.length > 0) {
      console.error('部分请求执行失败:', failedResults)
    }
  } catch (error) {
    console.error('请求执行失败:', error)
  } finally {
    spinning.value = false
  }
}
const getAllDataCc = async () => {
  spinning.value = true
  try {
    const results = await Promise.allSettled([
      getSellerCommissionReportsAggCc(),
      getListCc(),
      getTableListCc(),
    ])

    const failedResults = results.filter(
      (result) => result.status === 'rejected',
    )
    if (failedResults.length > 0) {
      console.error('部分请求执行失败:', failedResults)
    }
  } catch (error) {
    console.error('请求执行失败:', error)
  } finally {
    spinning.value = false
  }
}
onMounted(async () => {
  document.querySelector('.seller-performance-report').scrollIntoView()
  getChartWidth()
  getAllData()
  window.addEventListener('resize', function () {
    getChartWidth()
  })
})
</script>
<style lang="less" scoped>
.seller-performance-report {
  padding: 0 16px;

  .content-main {
    border-radius: 12px;
    overflow: hidden;
    border-radius: 12px;
    background-color: var(--color-white);
    box-shadow: var(--section-box-shadow-6);

    .tags {
      padding: 20px 12px;
    }

    .content-head {
      display: flex;
      justify-content: space-between;
      height: 64px;
      padding-left: 16px;
      padding-right: 16px;

      .content-head-left {
        display: flex;
        align-items: center;
        gap: 14px;
      }

      > p {
        font-size: 14px;
        color: var(--dark-2);
        line-height: 19px;
        align-self: center;
      }

      > button {
        align-self: center;
        cursor: pointer;
        display: flex;

        .jiantou {
          width: 16px;
          height: 16px;
          fill: var(--dark-3);
          margin-left: 8px;
        }

        .daochu {
          width: 20px;
          height: 20px;
          margin-right: 8px;
          fill: var(--dark-1);
        }

        > svg {
          align-self: center;
        }

        > span {
          align-self: center;
        }

        .btn-text {
          align-self: center;
          font-weight: 500;
          line-height: 20px;
          margin-right: 8px;
        }
      }
    }
  }

  .content {
    padding: 16px;

    .table {
      margin-top: 40px;
    }

    .chart {
      padding: 0 10px;
    }

    > p {
      font-weight: 600;
      font-size: 20px;
      color: var(--dark-1);
      letter-spacing: 0.5px;
      line-height: 28px;
      margin-bottom: 30px;
    }
  }
}
</style>
